import React from 'react';

import { mui } from '../libs';

type EmptyMessageType = 'empty' | 'warning' | 'error' | 'upgrade';

type EmptyMessageProps = {
  /** Content to display in the empty message */
  children: React.ReactNode;
  /** Optional title text to display */
  title?: string;
  /** Controls the icon and title color. Defaults to 'empty' */
  type?: EmptyMessageType;
  /** Optional icon override — replaces the default type-based icon */
  icon?: React.ReactNode;
  /** Optional action element (like a button) to display below content */
  action?: React.ReactNode;
  /** Whether to hide the border and box shadow styling */
  hideBorder?: boolean;
};

const getDefaultIcon = (type: EmptyMessageType): React.ReactNode => {
  if (type === 'warning') return <mui.icons.WarningRounded color="warning" />;
  if (type === 'error') return <mui.icons.ErrorRounded color="error" />;
  if (type === 'upgrade') return <mui.icons.RocketLaunch color="primary" />;
  return <mui.icons.InfoOutlined />;
};

const getTitleColor = (type: EmptyMessageType): string | undefined => {
  if (type === 'warning') return 'warning.main';
  if (type === 'error') return 'error.main';
  if (type === 'upgrade') return 'primary.main';
  return undefined;
};

const EmptyMessage = ({ title, type = 'empty', icon, action, hideBorder = false, children }: EmptyMessageProps) => {
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);
  }, []);

  const resolvedIcon = icon ?? getDefaultIcon(type);

  return (
    <mui.core.Box my={4}>
      <mui.core.Card
        variant="outlined"
        style={hideBorder ? { padding: '3rem', boxShadow: 'none', border: 0 } : {}}
        sx={{
          borderRadius: 4,
          background: hideBorder ? 'rgba(0,0,0,.001)' : '#fff',
          p: 5,
          textAlign: 'center',
          opacity: mounted ? 1 : 0,
          transition: 'opacity 0.5s ease-in-out',
        }}
      >
        <mui.core.IconButton sx={{ pointerEvents: 'none', '& svg': { fontSize: '2rem' } }}>
          {resolvedIcon}
        </mui.core.IconButton>
        {title && (
          <mui.core.Typography variant="h4" mt={1} mb={3} color={getTitleColor(type)}>
            {title}
          </mui.core.Typography>
        )}

        <mui.core.Typography variant="body2" color="textSecondary">
          {children}
        </mui.core.Typography>

        {action && <mui.core.Box mt={4}>{action}</mui.core.Box>}
      </mui.core.Card>
    </mui.core.Box>
  );
};

export default EmptyMessage;
